/**
 * v2 servicing Slice 9: BHPH Dashboard portal-requests panel — the D4-aware
 * urgent-promise call-out. Owns its own react-query state (independent of
 * the dashboard's collection/promise/late-fee queries per B5) so it renders
 * while those are still loading rather than being gated by them.
 */

import { useQuery } from '@tanstack/react-query';
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { staffPortalRequestsApi } from '../../../services/staffPortalRequestsApi';

const dueBadge = (proposedPromiseDate: string | null | undefined) => {
  if (!proposedPromiseDate) return null;
  // Local calendar date, NOT UTC -- proposed_promise_date is a bare local
  // date with no time component, and the row display below parses it as
  // local midnight (`T00:00:00`). Using toISOString() here would compare
  // against the UTC date instead, misflagging Overdue/Due-today for
  // several hours every evening in any US timezone.
  const now = new Date();
  const today = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
  if (proposedPromiseDate < today) {
    return <span className="text-xs font-semibold text-red-600">Overdue</span>;
  }
  if (proposedPromiseDate === today) {
    return <span className="text-xs font-semibold text-orange-600">Due today</span>;
  }
  return null;
};

const PortalRequestsPanel: React.FC = () => {
  const navigate = useNavigate();

  const { data: counts } = useQuery({
    queryKey: ['staffPortalRequestCounts'],
    queryFn: () => staffPortalRequestsApi.counts(),
    refetchInterval: 30000,
  });

  const { data: rows = [], isLoading } = useQuery({
    queryKey: [
      'staffPortalRequests',
      { promise_decision: 'pending', sort: 'proposed_promise_date' },
    ],
    queryFn: () =>
      staffPortalRequestsApi.list({
        promise_decision: 'pending',
        sort: 'proposed_promise_date',
        limit: 5,
      }),
  });

  return (
    <div className="bg-white rounded-lg shadow-md p-6 mb-8">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-semibold text-gray-900">Portal Requests</h2>
        <div className="flex items-center gap-3">
          {counts && (
            <span className="text-sm text-gray-500">
              {counts.pending_promises} pending promise{counts.pending_promises === 1 ? '' : 's'}
            </span>
          )}
          <button
            onClick={() => navigate('/bhph/portal-requests')}
            className="text-blue-600 hover:text-blue-800 text-sm font-medium"
          >
            View all
          </button>
        </div>
      </div>
      {isLoading ? (
        <div className="text-sm text-gray-500 py-4">Loading…</div>
      ) : rows.length === 0 ? (
        <div className="text-sm text-gray-500 py-4">No pending promises awaiting a decision.</div>
      ) : (
        <ul className="divide-y divide-gray-100">
          {rows.map((row) => (
            <li key={row.id} className="py-3 flex items-center justify-between gap-3">
              <div className="min-w-0">
                <p className="text-sm font-medium text-gray-900 truncate">
                  {row.customer_name || 'Unknown borrower'}
                  {row.account_number && (
                    <span className="text-gray-500 font-normal"> · {row.account_number}</span>
                  )}
                </p>
                <p className="text-xs text-gray-500 truncate">{row.subject}</p>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                {dueBadge(row.proposed_promise_date)}
                <span className="text-xs text-gray-500">
                  {row.proposed_promise_date
                    ? new Date(`${row.proposed_promise_date}T00:00:00`).toLocaleDateString()
                    : '—'}
                </span>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default PortalRequestsPanel;
